// frontend/src/pages/Products.jsx
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { getProducts } from "../api";
import { useAuth } from "../context/AuthContext";
import ProductCard from "../components/Product/ProductCard";
import Loader from "../components/UI/Loader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWhatsapp as faWhatsappBrand,
} from "@fortawesome/free-brands-svg-icons";
import {
  faSeedling,
  faFilter,
  faSearch,
  faTimes,
  faTractor,
  faSpinner,
  faMobileAlt,
  faMoneyBillWave,
  faCalendarCheck,
  faShippingFast,
  faCheckCircle,
  faGlobe,
  faBox,
  faTag,
  faStar,
  faClock,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";

// Import product images
import dairyBoostImg from "../assets/DairyBoost.png";
import mineralSaltImg from "../assets/Mineral-salt.png";
import growProImg from "../assets/growpro.png";
import darkImg from "../assets/dark.png";

// ============================================
// BRAND CONSTANTS
// ============================================
const BRAND = {
  primary: "#006B2E",
  primaryRgb: "0, 107, 46",
  secondary: "#D9A321",
  goldHover: "#B8860B",
  forest: "#014421",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  lightBg: "#F8F8F8",
  white: "#FFFFFF",
};

// ============================================
// FALLBACK PRODUCTS
// ============================================
const fallbackProducts = [
  {
    _id: "fallback1",
    name: "Kenali GrowPro™",
    slug: "kenali-growpro",
    unit: "kg",
    pricing: { usd: 1.3 },
    basePrice: { usd: 1.3 },
    stock: 100,
    featured: true,
    image: growProImg,
    category: "protein-concentrates",
    description:
      "Premium protein concentrate for livestock growth and muscle development.",
    shortDescription: "Premium protein concentrate for livestock growth.",
    farmerMinimumOrder: 25,
    origin: "Malaysia",
    supplierName: "Mylasha",
    availableSizes: [2, 10, 25, 50],
    preOrderSizes: [25, 50],
    isPreOrderAvailable: true,
  },
  {
    _id: "fallback2",
    name: "Kenali Mineralite™",
    slug: "kenali-mineralite",
    unit: "block",
    pricing: { usd: 2.5 },
    basePrice: { usd: 2.5 },
    stock: 50,
    featured: true,
    image: mineralSaltImg,
    category: "mineral-blocks",
    description:
      "Essential mineral salt blocks for livestock health and reproduction.",
    shortDescription: "Essential mineral salt blocks for livestock health.",
    farmerMinimumOrder: 50,
    origin: "Malaysia",
    supplierName: "Mylasha",
    availableSizes: [2, 10, 25, 50],
    preOrderSizes: [25, 50],
  },
  {
    _id: "fallback3",
    name: "Kenali DairyBoost™",
    slug: "kenali-dairyboost",
    unit: "kg",
    pricing: { usd: 1.8 },
    basePrice: { usd: 1.8 },
    stock: 75,
    featured: true,
    image: dairyBoostImg,
    category: "rumen-bypass-fat",
    description:
      "Premium dairy supplement for milk production and lactation support.",
    shortDescription: "Premium dairy supplement for milk production.",
    farmerMinimumOrder: 25,
    origin: "Malaysia",
    supplierName: "Mylasha",
    availableSizes: [2, 10, 25, 50],
    preOrderSizes: [25, 50],
  },
  {
    _id: "fallback4",
    name: "Kenali Feed Supplement Mix",
    slug: "kenali-feed-supplement-mix",
    unit: "kg",
    pricing: { usd: 1.5 },
    basePrice: { usd: 1.5 },
    stock: 100,
    featured: true,
    image: darkImg,
    category: "feed-supplements",
    description:
      "Complete feed supplement for optimal nutrition and animal performance.",
    shortDescription: "Complete feed supplement for optimal nutrition.",
    farmerMinimumOrder: 25,
    origin: "Malaysia",
    supplierName: "Mylasha",
    availableSizes: [2, 10, 25, 50],
    preOrderSizes: [25, 50],
  },
];

// ============================================
// CATEGORIES
// ============================================
const CATEGORIES = [
  { value: "", label: "All Products", icon: faBox },
  { value: "rumen-bypass-fat", label: "Rumen Bypass Fat", icon: faTag },
  { value: "mineral-blocks", label: "Mineral Blocks", icon: faStar },
  { value: "protein-concentrates", label: "Protein Concentrates", icon: faSeedling },
  { value: "feed-supplements", label: "Feed Supplements", icon: faCheckCircle },
];

// ============================================
// MAIN PRODUCTS COMPONENT
// ============================================
const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usingFallback, setUsingFallback] = useState(false);
  const [filters, setFilters] = useState({
    category: searchParams.get("category") || "",
    search: searchParams.get("search") || "",
  });
  const [searchInput, setSearchInput] = useState(filters.search);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const userType = user?.userType || "customer";
  const farmSize = user?.farmDetails?.farmSize || "small";
  const isFarmer = userType === "farmer";

  // ============================================
  // FETCH PRODUCTS
  // ============================================
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setUsingFallback(false);
      try {
        const response = await getProducts({
          ...filters,
          userType,
          farmSize,
          limit: 50,
        });
        const data = response.data;
        let productsData = data.products || [];

        if (productsData.length === 0) {
          let filteredFallback = [...fallbackProducts];
          if (filters.category) {
            filteredFallback = filteredFallback.filter(
              (p) => p.category === filters.category,
            );
          }
          if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            filteredFallback = filteredFallback.filter(
              (p) =>
                p.name.toLowerCase().includes(searchLower) ||
                p.description.toLowerCase().includes(searchLower),
            );
          }
          setProducts(filteredFallback);
          setUsingFallback(true);
        } else {
          setProducts(productsData);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
        let filteredFallback = [...fallbackProducts];
        if (filters.category) {
          filteredFallback = filteredFallback.filter(
            (p) => p.category === filters.category,
          );
        }
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          filteredFallback = filteredFallback.filter(
            (p) =>
              p.name.toLowerCase().includes(searchLower) ||
              p.description.toLowerCase().includes(searchLower),
          );
        }
        setProducts(filteredFallback);
        setUsingFallback(true);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [filters, userType, farmSize]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleSearch = (e) => {
    e.preventDefault();
    setFilters({ ...filters, search: searchInput });
    if (searchInput) {
      searchParams.set("search", searchInput);
    } else {
      searchParams.delete("search");
    }
    setSearchParams(searchParams);
  };

  const clearSearch = () => {
    setSearchInput("");
    setFilters({ ...filters, search: "" });
    searchParams.delete("search");
    setSearchParams(searchParams);
  };

  const handleCategoryFilter = (category) => {
    setFilters({ ...filters, category });
    if (category) {
      searchParams.set("category", category);
    } else {
      searchParams.delete("category");
    }
    setSearchParams(searchParams);
  };

  const clearAllFilters = () => {
    setFilters({ category: "", search: "" });
    setSearchInput("");
    searchParams.delete("category");
    searchParams.delete("search");
    setSearchParams(searchParams);
  };

  // ============================================
  // RENDER
  // ============================================
  if (loading) return <Loader />;

  return (
    <motion.div 
      className="products-page"
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* ============================================
          HERO SECTION
          ============================================ */}
      <section className="products-hero">
        <div className="products-hero-overlay" />
        <div className="container">
          <motion.div
            className="products-hero-content"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <FontAwesomeIcon icon={faSeedling} className="hero-icon" />
            <h1>Our Products</h1>
            <p>
              Premium livestock nutrition solutions sourced from Malaysia
            </p>
            <div className="hero-breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <span>Products</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============================================
          MAIN CONTENT
          ============================================ */}
      <div className="container products-content">
        {/* Pre-Order CTA */}
        <motion.div
          className="preorder-cta"
          initial={{ y: -10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
        >
          <div className="preorder-cta-content">
            <FontAwesomeIcon icon={faCalendarCheck} />
            <div>
              <strong>Pre-Order Kenali GrowPro™</strong>
              <span>50% deposit now, 50% on pickup.</span>
            </div>
          </div>
          <Link to="/preorder" className="preorder-cta-btn">
            Reserve Now <FontAwesomeIcon icon={faArrowRight} />
          </Link>
        </motion.div>

        {/* Fallback Notice */}
        {usingFallback && (
          <motion.div
            className="fallback-notice"
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
          >
            <FontAwesomeIcon icon={faClock} />
            <span>
              Showing sample products. Connect to database for live products.
            </span>
          </motion.div>
        )}

        {/* Farmer Notice */}
        {isFarmer && (
          <motion.div
            className="farmer-notice"
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
          >
            <FontAwesomeIcon icon={faTractor} />
            <div>
              <strong>🌾 Farmer Bulk Pricing Active!</strong>
              <span>Wholesale and bulk prices available. Minimum order: 25kg per product.</span>
            </div>
          </motion.div>
        )}

        {/* Payment Options Notice */}
        <motion.div
          className="payment-notice"
          initial={{ y: -10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
        >
          <FontAwesomeIcon icon={faMoneyBillWave} />
          <span>
            <strong>Flexible Payments:</strong> Cash on Delivery, Orange Money,
            Afrimoney, WAVE, Bank Transfer
          </span>
          <a
            href="https://wa.me/23231030808"
            target="_blank"
            rel="noopener noreferrer"
            className="whatsapp-link"
          >
            <FontAwesomeIcon icon={faWhatsappBrand} /> WhatsApp Us
          </a>
        </motion.div>

        {/* ============================================
            SEARCH & FILTER
            ============================================ */}
        <div className="search-filter-section">
          <div className="search-row">
            <form onSubmit={handleSearch} className="search-form">
              <div className="search-input-wrapper">
                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="search-input"
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="clear-search"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                )}
              </div>
              <button type="submit" className="search-btn">
                <FontAwesomeIcon icon={faSearch} /> Search
              </button>
            </form>

            <button
              className="filter-toggle"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            >
              <FontAwesomeIcon icon={faFilter} />
              <span>Filter</span>
            </button>
          </div>

          <div className={`filter-chips ${isFilterOpen ? "open" : ""}`}>
            {CATEGORIES.map((cat) => (
              <button
                key={cat.value}
                onClick={() => handleCategoryFilter(cat.value)}
                className={`filter-chip ${filters.category === cat.value ? "active" : ""}`}
              >
                <FontAwesomeIcon icon={cat.icon} />
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Results Count */}
        <div className="results-header">
          <span>Showing {products.length} products</span>
          {isFarmer && (
            <span className="bulk-badge">
              <FontAwesomeIcon icon={faTractor} /> Bulk pricing applied
            </span>
          )}
          {filters.category && (
            <button onClick={clearAllFilters} className="clear-filters">
              <FontAwesomeIcon icon={faTimes} /> Clear Filters
            </button>
          )}
        </div>

        {/* ============================================
            PRODUCT GRID
            ============================================ */}
        {products.length > 0 ? (
          <div className="products-grid">
            {products.map((product, index) => (
              <motion.div
                key={product._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <ProductCard
                  product={product}
                  userType={userType}
                  farmSize={farmSize}
                />
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">🔍</div>
            <h3>No products found</h3>
            <p>Try adjusting your search or filter criteria.</p>
            <button onClick={clearAllFilters} className="clear-filters-btn">
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* ============================================
          STYLES
          ============================================ */}
      <style>{`
        /* ========== PAGE ========== */
        .products-page {
          background: #ffffff;
        }

        /* ========== HERO ========== */
        .products-hero {
          background: linear-gradient(135deg, #006B2E 0%, #014421 100%);
          color: #FFFFFF;
          padding: clamp(2rem, 4vw, 3rem) 1.5rem;
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        .products-hero-overlay {
          position: absolute;
          top: -50%;
          right: -20%;
          width: 500px;
          height: 500px;
          background: rgba(217, 163, 33, 0.08);
          border-radius: 50%;
        }

        .products-hero-content {
          position: relative;
          z-index: 2;
        }

        .products-hero-content .hero-icon {
          font-size: clamp(2rem, 4vw, 2.5rem);
          color: #D9A321;
          margin-bottom: 0.75rem;
        }

        .products-hero-content h1 {
          font-size: clamp(1.5rem, 4vw, 2.5rem);
          margin-bottom: 0.5rem;
        }

        .products-hero-content p {
          font-size: clamp(0.875rem, 1.5vw, 1rem);
          max-width: 600px;
          margin: 0 auto;
          opacity: 0.9;
        }

        .hero-breadcrumb {
          margin-top: 0.75rem;
          font-size: 0.75rem;
          opacity: 0.7;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .hero-breadcrumb a {
          color: #D9A321;
          text-decoration: none;
        }

        .hero-breadcrumb a:hover {
          text-decoration: underline;
        }

        /* ========== CONTENT ========== */
        .products-content {
          padding: clamp(1.5rem, 3vw, 3rem) 1rem;
        }

        /* ========== PRE-ORDER CTA ========== */
        .preorder-cta {
          background: linear-gradient(135deg, #063D1C, #0B5E2D);
          color: #FFFFFF;
          padding: 0.75rem 1.25rem;
          border-radius: 12px;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 0.75rem;
        }

        .preorder-cta-content {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .preorder-cta-content svg {
          color: #D9A321;
          font-size: 1.1rem;
        }

        .preorder-cta-content strong {
          font-size: clamp(0.8rem, 1.2vw, 0.938rem);
        }

        .preorder-cta-content span {
          opacity: 0.9;
          font-size: clamp(0.7rem, 1vw, 0.813rem);
        }

        .preorder-cta-btn {
          padding: 0.3rem 1rem;
          background: #D9A321;
          color: #FFFFFF;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 600;
          font-size: clamp(0.75rem, 1vw, 0.875rem);
          transition: all 0.3s ease;
          white-space: nowrap;
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
        }

        .preorder-cta-btn:hover {
          background: #B8860B;
          transform: translateY(-2px);
        }

        /* ========== NOTICES ========== */
        .fallback-notice {
          background: #fef3c7;
          color: #d97706;
          padding: 0.5rem 0.75rem;
          border-radius: 8px;
          text-align: center;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          flex-wrap: wrap;
          font-size: clamp(0.7rem, 1vw, 0.813rem);
        }

        .farmer-notice {
          background: linear-gradient(135deg, #006B2E 0%, #014421 100%);
          color: #FFF;
          padding: 0.75rem 1rem;
          border-radius: 12px;
          text-align: center;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          flex-wrap: wrap;
          font-size: clamp(0.75rem, 1vw, 0.875rem);
        }

        .farmer-notice svg {
          font-size: 1.25rem;
        }

        .farmer-notice strong {
          color: #D9A321;
        }

        .payment-notice {
          background: #e8f5e9;
          padding: 0.5rem 0.75rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          flex-wrap: wrap;
          border: 1px solid #c8e6c9;
          font-size: clamp(0.7rem, 1vw, 0.813rem);
          color: #006B2E;
        }

        .payment-notice .whatsapp-link {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          color: #25d366;
          font-weight: 600;
          text-decoration: none;
        }

        .payment-notice .whatsapp-link:hover {
          text-decoration: underline;
        }

        /* ========== SEARCH & FILTER ========== */
        .search-filter-section {
          margin-bottom: 1.5rem;
        }

        .search-row {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
          align-items: center;
        }

        .search-form {
          display: flex;
          gap: 0.5rem;
          flex: 1;
          max-width: 500px;
        }

        .search-input-wrapper {
          position: relative;
          flex: 1;
        }

        .search-icon {
          position: absolute;
          left: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          color: #9ca3af;
          font-size: 0.875rem;
        }

        .search-input {
          width: 100%;
          padding: 0.5rem 0.875rem;
          padding-left: 2.5rem;
          padding-right: 2.5rem;
          border: 2px solid #e5e5e5;
          border-radius: 8px;
          font-size: clamp(0.813rem, 1vw, 0.938rem);
          outline: none;
          transition: border-color 0.3s ease;
          background: #ffffff;
          color: #1f1f1f;
          min-height: 44px;
          font-family: "Inter", sans-serif;
        }

        .search-input:focus {
          border-color: #D9A321;
          box-shadow: 0 0 0 3px rgba(217, 163, 33, 0.15);
        }

        .clear-search {
          position: absolute;
          right: 0.5rem;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          color: #6B7280;
          padding: 0.25rem;
          font-size: 0.875rem;
        }

        .search-btn {
          padding: 0.5rem 1.25rem;
          background: #D9A321;
          color: #FFFFFF;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: clamp(0.813rem, 1vw, 0.938rem);
          white-space: nowrap;
          min-height: 44px;
          font-family: "Inter", sans-serif;
        }

        .search-btn:hover {
          background: #B8860B;
          transform: translateY(-2px);
        }

        .filter-toggle {
          padding: 0.5rem 1rem;
          background: #F8F8F8;
          border: 1px solid #e5e5e5;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 500;
          font-size: 0.875rem;
          transition: all 0.3s ease;
          min-height: 44px;
          font-family: "Inter", sans-serif;
          color: #1F1F1F;
        }

        .filter-toggle:hover {
          background: #f0f0f0;
          border-color: #D9A321;
        }

        .filter-chips {
          display: flex;
          gap: 0.4rem;
          flex-wrap: wrap;
          margin-top: 0.75rem;
          overflow: hidden;
          max-height: 0;
          opacity: 0;
          transition: all 0.3s ease;
        }

        .filter-chips.open {
          max-height: 300px;
          opacity: 1;
        }

        .filter-chip {
          padding: 0.3rem 0.8rem;
          background: #F8F8F8;
          color: #5A5A5A;
          border: 2px solid transparent;
          border-radius: 20px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.3rem;
          font-weight: 400;
          font-size: clamp(0.7rem, 0.9vw, 0.813rem);
          font-family: "Inter", sans-serif;
        }

        .filter-chip:hover {
          background: #e8f5e9;
        }

        .filter-chip.active {
          background: #D9A321;
          color: #FFFFFF;
          border-color: #D9A321;
          font-weight: 600;
        }

        .filter-chip svg {
          font-size: 0.6rem;
        }

        /* ========== RESULTS ========== */
        .results-header {
          margin-bottom: 1rem;
          color: #6B7280;
          font-size: clamp(0.75rem, 0.9vw, 0.875rem);
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .bulk-badge {
          font-size: clamp(0.65rem, 0.8vw, 0.75rem);
          color: #006B2E;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .clear-filters {
          background: none;
          border: none;
          color: #D9A321;
          cursor: pointer;
          font-size: 0.75rem;
          display: flex;
          align-items: center;
          gap: 0.25rem;
          font-family: "Inter", sans-serif;
          font-weight: 500;
        }

        .clear-filters:hover {
          color: #B8860B;
        }

        /* ========== PRODUCT GRID ========== */
        .products-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
          gap: clamp(1rem, 2vw, 2rem);
        }

        /* ========== EMPTY STATE ========== */
        .empty-state {
          text-align: center;
          padding: 3rem 1rem;
          color: #6B7280;
        }

        .empty-icon {
          font-size: 3rem;
          margin-bottom: 1rem;
        }

        .empty-state h3 {
          color: #1F1F1F;
          margin-bottom: 0.5rem;
        }

        .empty-state p {
          color: #5A5A5A;
          margin-bottom: 1rem;
        }

        .clear-filters-btn {
          padding: 0.5rem 1.5rem;
          background: #D9A321;
          color: #FFF;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.3s ease;
          font-family: "Inter", sans-serif;
          font-size: clamp(0.813rem, 1vw, 0.938rem);
        }

        .clear-filters-btn:hover {
          background: #B8860B;
        }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 768px) {
          .search-row {
            flex-direction: column;
          }

          .search-form {
            max-width: 100%;
            width: 100%;
          }

          .filter-toggle {
            width: 100%;
            justify-content: center;
          }

          .filter-chips {
            justify-content: center;
          }

          .products-grid {
            grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
            gap: 1rem;
          }
        }

        @media (max-width: 480px) {
          .products-grid {
            grid-template-columns: 1fr 1fr;
            gap: 0.75rem;
          }

          .preorder-cta {
            flex-direction: column;
            text-align: center;
          }

          .preorder-cta-btn {
            width: 100%;
            justify-content: center;
          }

          .payment-notice {
            flex-direction: column;
            text-align: center;
          }

          .farmer-notice {
            flex-direction: column;
            text-align: center;
          }

          .results-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.25rem;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default Products;